import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
//import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import { LanguageProvider } from './i18n/LocalizationContext';
import TranslationDomBridge from './i18n/TranslationDomBridge';
import DialogBehaviorGuard from './components/Common/DialogBehaviorGuard';


import Login from './components/Auth/Login';
import EmployeeRegistration from './components/Auth/EmployeeRegistration';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import EmployeeDashboardPage from './pages/EmployeeDashboardPage';
import NoPermissionsPage from './pages/NoPermissionsPage';
import ForbiddenPage from './pages/ForbiddenPage';
import EmployeeList from './components/Employees/EmployeeList';
import EmployeeDirectory from './components/Employees/EmployeeDirectory';
import EmployeeProfile from './components/Employees/EmployeeProfile';
import RegistrationRequests from './components/Employees/RegistrationRequests';
import EmployeeBulkUpload from './components/Employees/EmployeeBulkUpload';
import AttendancePanel from './components/Attendance/AttendancePanel';
import EmployeePunchesModule from './components/EmployeePunches/EmployeePunchesModule';
import LocationList from './components/Locations/LocationList';
import LocationCategories from './components/Locations/LocationCategories';
import LocationWhitelistPage from './components/Locations/LocationWhitelist';


// Leave Management
import LeaveRequests from './components/Leave/LeaveRequests';
import AssignLeave from './components/Leave/AssignLeave';
import Holidays from './components/Leave/Holidays';
import LeaveEntitlements from './components/Leave/LeaveEntitlements';
import LeaveUsageReport from './components/Leave/LeaveUsageReport';
import EmployeeLeaveModule from './components/Leave/EmployeeLeavePage';
import LeaveCalendarPage from './pages/LeaveCalendarPage';


// Attendance Module Pages
import AttendanceRecordsPage from './modules/attendance/pages/AttendanceRecordsPage';
import RegularizationRequestsPage from './modules/attendance/pages/RegularizationRequestsPage';
import AttendanceReportPage from './modules/attendance/pages/AttendanceReportPage';
import ManualOTPage from './modules/attendance/pages/ManualOTPage';
import ShiftManagementPage from './modules/attendance/pages/ShiftManagementPage';
import LeavePeriodPage from './modules/attendance/pages/LeavePeriodPage';
import LeaveTypesPage from './modules/attendance/pages/LeaveTypesPage';
import AttendanceLeaveSettingsPage from './modules/attendance/pages/AttendanceLeaveSettingsPage';
import AttendanceRulesPage from './modules/attendance/pages/AttendanceRulesPage';
import PunchSettingsPage from './modules/attendance/pages/PunchSettingsPage';
import BiometricManagementPage from './modules/attendance/pages/BiometricManagementPage';
import FaceRegistrationPage from './modules/attendance/pages/FaceRegistrationPage';
import FacePunchPage from './modules/attendance/pages/FacePunchPage';
import KioskPage from './modules/attendance/pages/KioskPage';
import AttendanceReportFilterForm from './modules/attendance/components/AttendanceReportFilterForm';
//import AccountSettingsManagement from './components/AccountSettings/AccountSettingsManagement';


// Activity Logs Module
import ViewAllPunchesPage from './modules/activityLogs/pages/ViewAllPunchesPage';
import DocumentManagementSettingsPage from './pages/DocumentManagementSettingsPage';
import GeneralDefaultSettingPage from './pages/GeneralDefaultSettingPage';
import MasterListManagementPage from './pages/MasterListManagementPage';


import ToolsPage from './pages/ToolsPage';
import PermissionsManagementPage from './pages/PermissionsManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';
import BillingPage from './pages/BillingPage';
import HelpTicketsPage from './pages/HelpTicketsPage';
import UserNotificationsPage from './pages/UserNotificationsPage';
import ReportsPage from './pages/ReportsPage';
import AttendanceStatusList from './pages/AttendanceStatusList';


// Payroll Module
import PayrollDashboardPage from './modules/payroll/pages/PayrollDashboardPage';
import PayrollRunsPage from './modules/payroll/pages/PayrollRunsPage';
import PayrollRunDetailPage from './modules/payroll/pages/PayrollRunDetailPage';
import SalaryComponentsPage from './modules/payroll/pages/SalaryComponentsPage';
import PayrollGroupAssignmentsPage from './modules/payroll/pages/PayrollGroupAssignmentsPage';
import PayrollSettingsPage from './modules/payroll/pages/PayrollSettingsPage';
import TaxDeclarationsPage from './modules/payroll/pages/TaxDeclarationsPage';
import PayslipsPage from './modules/payroll/pages/PayslipsPage';
import MySalaryPage from './modules/payroll/pages/MySalaryPage';
import MyPayslipsPage from './modules/payroll/pages/MyPayslipsPage';
import MyTaxDeclarationsPage from './modules/payroll/pages/MyTaxDeclarationsPage';


// BGV Module
import BgvDashboardPage from './modules/bgv/pages/BgvDashboardPage';
import BgvVerificationListPage from './modules/bgv/pages/BgvVerificationListPage';
import BgvVerificationDetailsPage from './modules/bgv/pages/BgvVerificationDetailsPage';
import BgvCreateVerificationPage from './modules/bgv/pages/BgvCreateVerificationPage';
import BgvVendorManagementPage from './modules/bgv/pages/BgvVendorManagementPage';


// Geo Location Module
import LocationHistoryPage from './modules/geoLocation/pages/LocationHistoryPage';
import LocationReportPage from './modules/geoLocation/pages/LocationReportPage';


// Super Admin Module
import SuperAdminLayout from './modules/superAdmin/layout/SuperAdminLayout';
import SuperAdminDashboardPage from './modules/superAdmin/pages/SuperAdminDashboardPage';
import TenantManagementPage from './modules/superAdmin/pages/TenantManagementPage';
import SubscriptionPlansPage from './modules/superAdmin/pages/SubscriptionPlansPage';
import BillingInvoicePage from './modules/superAdmin/pages/BillingInvoicePage';
import SupportTicketsPage from './modules/superAdmin/pages/SupportTicketsPage';
import FeatureFlagsPage from './modules/superAdmin/pages/FeatureFlagsPage';
import RoleTemplatesPage from './modules/superAdmin/pages/RoleTemplatesPage';
import PlatformAnalyticsPage from './modules/superAdmin/pages/PlatformAnalyticsPage';
import NotificationsPage from './modules/superAdmin/pages/NotificationsPage';
import AuditLogsPage from './modules/superAdmin/pages/AuditLogsPage';
import SecurityMonitoringPage from './modules/superAdmin/pages/SecurityMonitoringPage';
import UsageMonitoringPage from './modules/superAdmin/pages/UsageMonitoringPage';
import SystemSettingsPage from './modules/superAdmin/pages/SystemSettingsPage';
import AccountSettingsManagement from './components/AccountSettings/AccountSettingsManagement';
import {
  getDefaultAuthorizedRoute,
  getFeatureKeyForPath,
  getPayrollDefaultRoute,
  hasFeatureAccess,
  hasPermissionAccess,
  isTenantAdminUser,
  FORBIDDEN_ROUTE,
  NO_PERMISSIONS_ROUTE,
  userHasAnyAssignedPermission,
} from './utils/permissionAccess';
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
});


const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};


const PermissionRoute: React.FC<{
  children: React.ReactNode;
  permission?: string;
  anyPermission?: string[];
  requireAnyAssignedPermission?: boolean;
  disallowTenantAdmin?: boolean;
}> = ({ children, permission, anyPermission, requireAnyAssignedPermission, disallowTenantAdmin }) => {
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = user?.permissions ?? [];
  const roles = [...(user?.roles ?? []), ...(user?.roleName ? [user.roleName] : [])];
  const requiredFeature = getFeatureKeyForPath(location.pathname);


  const hasAssignedPermission = userHasAnyAssignedPermission(user);
  const deniedRoute = hasAssignedPermission ? FORBIDDEN_ROUTE : NO_PERMISSIONS_ROUTE;
  const safeDeniedRoute = deniedRoute === location.pathname ? getDefaultAuthorizedRoute(user) : deniedRoute;


  if (requireAnyAssignedPermission && !hasAssignedPermission) {
    return <Navigate to={NO_PERMISSIONS_ROUTE} replace />;
  }
  if (!hasFeatureAccess(requiredFeature, user?.enabledFeatures)) {
    return <Navigate to={safeDeniedRoute} replace />;
  }
  if (disallowTenantAdmin && isTenantAdminUser(roles)) {
    return <Navigate to={safeDeniedRoute} replace />;
  }
  if (permission && !hasPermissionAccess(permission, permissions, roles)) {
    return <Navigate to={safeDeniedRoute} replace />;
  }
  if (anyPermission && !anyPermission.some((p) => hasPermissionAccess(p, permissions, roles))) {
    return <Navigate to={safeDeniedRoute} replace />;
  }
  return <>{children}</>;
};


const DashboardRoute: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = user?.permissions ?? [];

  if (!hasFeatureAccess(getFeatureKeyForPath('/dashboard'), user?.enabledFeatures)) {
    return <Navigate to={getDefaultAuthorizedRoute(user)} replace />;
  }

  if (permissions.includes('ViewDashboard')) {
    return <DashboardPage />;
  }


  if (permissions.includes('ViewEmployeeDashboard')) {
    return <EmployeeDashboardPage />;
  }


  if (!userHasAnyAssignedPermission(user)) {
    return <Navigate to={NO_PERMISSIONS_ROUTE} replace />;
  }


  return <Navigate to={getDefaultAuthorizedRoute(user)} replace />;
};

const NoPermissionsRoute: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);

  if (userHasAnyAssignedPermission(user)) {
    return <Navigate to={getDefaultAuthorizedRoute(user)} replace />;
  }

  return <NoPermissionsPage />;
};

const ForbiddenRoute: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);

  if (!userHasAnyAssignedPermission(user)) {
    return <Navigate to={NO_PERMISSIONS_ROUTE} replace />;
  }

  return <ForbiddenPage />;
};

const DefaultAuthorizedRoute: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  return <Navigate to={getDefaultAuthorizedRoute(user)} replace />;
};

const AppFallbackRoute: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  return <Navigate to={isAuthenticated ? getDefaultAuthorizedRoute(user) : '/login'} replace />;
};

const PayrollRedirect: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = user?.permissions ?? [];
  const roles = user?.roles ?? [];

  if (!hasFeatureAccess(getFeatureKeyForPath('/payroll'), user?.enabledFeatures)) {
    return <Navigate to={getDefaultAuthorizedRoute(user)} replace />;
  }

  if (!userHasAnyAssignedPermission(user)) {
    return <Navigate to={NO_PERMISSIONS_ROUTE} replace />;
  }

  return <Navigate to={getPayrollDefaultRoute(permissions, roles)} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <TranslationDomBridge />
        <DialogBehaviorGuard />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
        <NetworkStatusBanner />
        <Router>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/employee-registration" element={<EmployeeRegistration />} />
          <Route path="/attendance/kiosk" element={<KioskPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DefaultAuthorizedRoute />} />
            <Route path="no-permissions" element={<NoPermissionsRoute />} />
            <Route path="forbidden" element={<ForbiddenRoute />} />
            <Route path="dashboard" element={<DashboardRoute />} />
            <Route path="employees" element={<PermissionRoute permission="ViewEmployee"><EmployeeList /></PermissionRoute>} />
            <Route path="employees/directory" element={<PermissionRoute permission="ViewEmployee"><EmployeeDirectory /></PermissionRoute>} />
            <Route path="employees/registration-requests" element={<PermissionRoute permission="ViewEmployee"><RegistrationRequests /></PermissionRoute>} />
            <Route path="employees/upLoadBulkData" element={<PermissionRoute permission="CreateEmployee"><EmployeeBulkUpload /></PermissionRoute>} />
            <Route path="my-profile/:id/:section?" element={<PermissionRoute requireAnyAssignedPermission><EmployeeProfile /></PermissionRoute>} />
            <Route path="admin/employee/manage/:id/:section?" element={<PermissionRoute permission="ViewEmployee"><EmployeeProfile /></PermissionRoute>} />
            <Route path="attendance" element={<PermissionRoute permission="ViewAttendance"><AttendancePanel /></PermissionRoute>} />
            <Route path="attendance/face-registration" element={<PermissionRoute permission="ViewAttendance"><FaceRegistrationPage /></PermissionRoute>} />
            <Route path="attendance/face-punch" element={<PermissionRoute permission="ViewAttendance"><FacePunchPage /></PermissionRoute>} />
            <Route path="employee-punches" element={<PermissionRoute permission="ViewAttendance"><EmployeePunchesModule /></PermissionRoute>} />
            <Route path="locations" element={<PermissionRoute permission="ViewLocation"><LocationList /></PermissionRoute>} />
            <Route path="location-categories" element={<PermissionRoute permission="ViewLocation"><LocationCategories /></PermissionRoute>} />
            <Route path="location-whitelist" element={<PermissionRoute permission="ManageLocation"><LocationWhitelistPage /></PermissionRoute>} />
            <Route path="geo-location/history" element={<PermissionRoute anyPermission={['ViewGeoLocation', 'ViewOwnGeoLocation']}><LocationHistoryPage /></PermissionRoute>} />
            <Route path="geo-location/report" element={<PermissionRoute anyPermission={['ViewGeoLocation', 'ViewOwnGeoLocation']}><LocationReportPage /></PermissionRoute>} />
            <Route path="settings/permissions" element={<PermissionRoute permission="ManagePermissions"><PermissionsManagementPage /></PermissionRoute>} />
            <Route path="settings/roles" element={<PermissionRoute permission="ManageRoles"><RoleManagementPage /></PermissionRoute>} />
            <Route path="document-management-settings" element={<PermissionRoute permission="ManageSettings"><DocumentManagementSettingsPage /></PermissionRoute>} />
            <Route path="genral-default-setting" element={<PermissionRoute permission="ManageSettings"><GeneralDefaultSettingPage /></PermissionRoute>} />
            {/* <Route path="master-list-management" element={<PermissionRoute permission="ViewMaster"><MasterListManagementPage /></PermissionRoute>} /> */}
            <Route path="admin/announcements" element={<PermissionRoute permission="ManageTools"><ToolsPage defaultTab="announcements" /></PermissionRoute>} />
            <Route path="admin/org-setup/hrms-forward-config" element={<PermissionRoute permission="ManageTools"><ToolsPage defaultTab="forward-settings" /></PermissionRoute>} />


            <Route path="help/my-tickets" element={<PermissionRoute requireAnyAssignedPermission><HelpTicketsPage /></PermissionRoute>} />
            <Route path="notifications" element={<PermissionRoute permission="ViewNotifications"><UserNotificationsPage /></PermissionRoute>} />
            <Route path="reports" element={<PermissionRoute anyPermission={['ViewReports', 'ViewOwnReports']}><ReportsPage /></PermissionRoute>} />



            {/* Leave Management Routes */}
            <Route path="leave/my-leave" element={<PermissionRoute permission="ViewLeave" disallowTenantAdmin><EmployeeLeaveModule /></PermissionRoute>} />
            <Route path="leave/requests" element={<PermissionRoute anyPermission={['ViewLeaveRequests', 'ApproveLeave']}><LeaveRequests /></PermissionRoute>} />
            <Route path="leave/assign" element={<PermissionRoute permission="ManageLeave"><AssignLeave /></PermissionRoute>} />
            <Route path="leave/holidays" element={<PermissionRoute permission="ManageLeave"><Holidays /></PermissionRoute>} />
            <Route path="leave/entitlements" element={<PermissionRoute permission="ManageLeave"><LeaveEntitlements /></PermissionRoute>} />
            <Route path="leave/usage-report" element={<PermissionRoute permission="ViewLeaveUsageReport"><LeaveUsageReport /></PermissionRoute>} />
            <Route path="leave/leave-calendar" element={<PermissionRoute permission="ViewLeave"><LeaveCalendarPage /></PermissionRoute>} />
            {/* Attendance & Shift Management Routes */}
            <Route path="attendance/records" element={<PermissionRoute permission="ViewAttendanceRecords"><AttendanceRecordsPage /></PermissionRoute>} />
            <Route
              path="attendance/regularization-request"
              element={
                <PermissionRoute anyPermission={['ViewAttendance', 'ViewAttendanceRecords']}>
                  <AttendanceReportFilterForm />
                </PermissionRoute>
              }
            />
            <Route path="attendance/regularization" element={<PermissionRoute permission="ViewRegularizationRequests"><RegularizationRequestsPage /></PermissionRoute>} />
            <Route path="attendance/report" element={<PermissionRoute permission="ViewAttendanceReports"><AttendanceReportPage /></PermissionRoute>} />
            <Route path="attendance/manual-ot" element={<PermissionRoute permission="ManualOT"><ManualOTPage /></PermissionRoute>} />
            <Route path="attendance/shift/*" element={<PermissionRoute permission="ViewShifts"><ShiftManagementPage /></PermissionRoute>} />
            <Route path="attendance/leave-period/*" element={<PermissionRoute permission="ManageLeave"><LeavePeriodPage /></PermissionRoute>} />
            <Route path="attendance/leave-types/*" element={<PermissionRoute permission="ManageLeave"><LeaveTypesPage /></PermissionRoute>} />
            <Route path="attendance/leave-settings/*" element={<PermissionRoute permission="ManageShifts"><AttendanceLeaveSettingsPage /></PermissionRoute>} />
            <Route path="attendance/settings/*" element={<PermissionRoute permission="ManageShifts"><AttendanceLeaveSettingsPage /></PermissionRoute>} />
            <Route path="attendance/rules" element={<PermissionRoute permission="ManageShifts"><AttendanceRulesPage /></PermissionRoute>} />
            <Route path="attendance/entitled-setting" element={<PermissionRoute permission="ManageShifts"><AttendanceRulesPage /></PermissionRoute>} />
            <Route path="attendance/advance-setting" element={<PermissionRoute permission="ManageShifts"><AttendanceRulesPage /></PermissionRoute>} />
            <Route path="attendance/auto-process" element={<PermissionRoute permission="ManageShifts"><AttendanceRulesPage /></PermissionRoute>} />
            <Route path="attendance/punch-settings" element={<PermissionRoute permission="ManageShifts"><PunchSettingsPage /></PermissionRoute>} />
            <Route path="attendance/biometric" element={<PermissionRoute permission="ManageBiometric"><BiometricManagementPage /></PermissionRoute>} />


            {/* Account Settings & Management Routes */}
            <Route path="settings/account-management" element={<PermissionRoute permission="ManageSettings"><Navigate to="/settings/account-management/organization" replace /></PermissionRoute>} />
            <Route path="settings/account-management/organization" element={<PermissionRoute permission="ManageSettings"><AccountSettingsManagement section="organization" /></PermissionRoute>} />
            <Route path="settings/account-management/branches" element={<PermissionRoute permission="ManageSettings"><AccountSettingsManagement section="branches" /></PermissionRoute>} />
            <Route path="settings/account-management/departments" element={<PermissionRoute permission="ManageSettings"><AccountSettingsManagement section="departments" /></PermissionRoute>} />
            <Route path="settings/account-management/designations" element={<PermissionRoute permission="ManageSettings"><AccountSettingsManagement section="designations" /></PermissionRoute>} />
            <Route path="settings/account-management/account-information" element={<PermissionRoute permission="ManageSettings"><Navigate to="/settings/account-management/organization" replace /></PermissionRoute>} />
            <Route path="settings/account-management/branding" element={<PermissionRoute permission="ManageSettings"><Navigate to="/settings/account-management/organization" replace /></PermissionRoute>} />

            {/* Billing Route */}
            <Route path="billing" element={<PermissionRoute permission="ViewBilling"><BillingPage /></PermissionRoute>} />


            {/* Payroll Routes */}
            <Route path="payroll" element={<PayrollRedirect />} />
            <Route path="payroll/dashboard" element={<PermissionRoute anyPermission={['ViewPayroll', 'ManagePayroll']}><PayrollDashboardPage /></PermissionRoute>} />
            <Route path="payroll/runs" element={<PermissionRoute anyPermission={['ViewPayroll', 'ProcessPayroll']}><PayrollRunsPage /></PermissionRoute>} />
            <Route path="payroll/runs/:id" element={<PermissionRoute anyPermission={['ViewPayroll', 'ProcessPayroll']}><PayrollRunDetailPage /></PermissionRoute>} />
            <Route path="payroll/salary-components" element={<PermissionRoute permission="ManagePayroll"><SalaryComponentsPage /></PermissionRoute>} />
            <Route path="payroll/group-assignments" element={<PermissionRoute permission="ManagePayroll"><PayrollGroupAssignmentsPage /></PermissionRoute>} />
            <Route path="payroll/tax-declarations" element={<PermissionRoute anyPermission={['ViewPayroll', 'ManagePayroll']}><TaxDeclarationsPage /></PermissionRoute>} />
            <Route path="payroll/payslips" element={<PermissionRoute permission="ViewPayroll"><PayslipsPage /></PermissionRoute>} />
            <Route path="payroll/settings" element={<PermissionRoute permission="ManagePayroll"><PayrollSettingsPage /></PermissionRoute>} />
            {/* Payroll Employee Self-Service */}
            <Route path="payroll/my-salary" element={<PermissionRoute permission="ViewMySalary"><MySalaryPage /></PermissionRoute>} />
            <Route path="payroll/my-payslips" element={<PermissionRoute permission="ViewMyPayslip"><MyPayslipsPage /></PermissionRoute>} />
            <Route path="payroll/my-tax-declarations" element={<PermissionRoute permission="ViewMyTaxDeclaration"><MyTaxDeclarationsPage /></PermissionRoute>} />


            {/* BGV Routes */}
            <Route path="bgv/dashboard" element={<PermissionRoute anyPermission={['ViewBGV', 'ManageBGV']}><BgvDashboardPage /></PermissionRoute>} />
            <Route path="bgv/cases" element={<PermissionRoute anyPermission={['ViewBGV', 'ManageBGV']}><BgvVerificationListPage /></PermissionRoute>} />
            <Route path="bgv/cases/:id" element={<PermissionRoute anyPermission={['ViewBGV', 'ManageBGV']}><BgvVerificationDetailsPage /></PermissionRoute>} />
            <Route path="bgv/assigned-cases" element={<PermissionRoute permission="ViewAssignedBGV"><BgvVerificationListPage /></PermissionRoute>} />
            <Route path="bgv/assigned-cases/:id" element={<PermissionRoute permission="ViewAssignedBGV"><BgvVerificationDetailsPage /></PermissionRoute>} />
            <Route path="bgv/create" element={<PermissionRoute permission="ManageBGV"><BgvCreateVerificationPage /></PermissionRoute>} />
            <Route path="bgv/vendors" element={<PermissionRoute permission="ManageBGV"><BgvVendorManagementPage /></PermissionRoute>} />


            {/* Activity Logs Routes */}
            <Route path="activity-logs/view-all-punches" element={<PermissionRoute permission="ViewActivityLogs"><ViewAllPunchesPage /></PermissionRoute>} />
          </Route>


          {/* Super Admin Routes */}
          <Route
            path="super-admin"
            element={
              <ProtectedRoute>
                <PermissionRoute permission="SuperAdmin">
                  <SuperAdminLayout />
                </PermissionRoute>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
            <Route path="dashboard" element={<SuperAdminDashboardPage />} />
            <Route path="tenants" element={<TenantManagementPage />} />
            <Route path="subscription-plans" element={<SubscriptionPlansPage />} />
            <Route path="billing" element={<BillingInvoicePage />} />
            <Route path="support-tickets" element={<SupportTicketsPage />} />
            <Route path="feature-flags" element={<FeatureFlagsPage />} />
            <Route path="role-templates" element={<RoleTemplatesPage />} />
            <Route path="analytics" element={<PlatformAnalyticsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="audit-logs" element={<AuditLogsPage />} />
            <Route path="security" element={<SecurityMonitoringPage />} />
            <Route path="usage" element={<UsageMonitoringPage />} />
            <Route path="settings" element={<SystemSettingsPage />} />
          </Route>

          <Route path="*" element={<AppFallbackRoute />} />
          </Routes>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
